import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NotificationService } from '@progress/kendo-angular-notification';
import { DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import { take } from 'rxjs';
import { ContributorRequest } from 'src/app/shared/models/add-contributor.model';
import { AuthStore } from 'src/app/stores/auth.store';
import { ContributorService } from 'src/app/shared/services/contributor-service';
import {
  dialogAnimationDuration,
  dialogTimer,
} from 'src/app/shared/constants/dialog-specs';
import { SVGIcon, trashIcon } from '@progress/kendo-svg-icons';
import { IIepContributor } from 'src/app/modules/iep/iep-document/interfaces/iep-contributor';
import { filter, get, head, map } from 'lodash';
import { IIepStaff } from 'src/app/modules/iep/iep-document/interfaces/iep-staff';
import { SubmitContributorData } from 'src/app/shared/models/submit-contributor.model';
import { SubmitProgressReportContributorData } from 'src/app/shared/models/submit-progress-report-contributor.model';
import { DocumentStatus } from 'src/app/modules/referrals/interfaces/referrals.model';
import { User } from 'src/app/shared/models/user';
import { NOTIFICATION_MESSAGES } from 'src/app/shared/constants/notification-messages';
import { OperationType } from 'src/app/shared/enums/operation-type';

@Component({
    selector: 'app-progress-report-contributor-dailogbox',
    templateUrl: './progress-report-contributor-dailogbox.component.html',
    styleUrls: ['./progress-report-contributor-dailogbox.component.scss'],
    standalone: false,
    encapsulation: ViewEncapsulation.None
})
export class ProgressReportContributorDailogboxComponent
  extends DialogContentBase
  implements OnInit
{
  public opened: boolean = false;
  form: FormGroup;
  existingContributorsList: IIepContributor[];
  iepFormSk: string;
  progressReportSk?: string;
  progressMeasurementSk?: string;
  mandateSK?: string;
  annualGoalSeq?: number;

  sectionId: number;
  contributors: IIepStaff[];
  dbn: string = '24Q610';
  contributorName: string;
  isReadOnly: boolean = false;
  public remove: SVGIcon = trashIcon;
  submitData: SubmitContributorData[] = [];
  user: User;
  readonly operationTypeRemove: OperationType = OperationType.Delete;
  readonly operationTypeSave: OperationType = OperationType.Add;

  constructor(
    private fb: FormBuilder,
    private contributorService: ContributorService,
    private notificationService: NotificationService,
    dialog: DialogRef,
    private authStore: AuthStore
  ) {
    super(dialog);
    this.user = this.authStore.UserInfo;
  }

  ngOnInit(): void {
    this.form = this.fb.group({ addContributor: [[]] });
    this.contributorService
      .getContributors(this.dbn)
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      .pipe(take(1))
      .subscribe((data: IIepStaff[]) => {
        this.contributors = data;
      });
  }

  public close(): void {
    this.dialog.close();
  }

  removeContibutor(data: ContributorRequest): void {
    if (this.isReadOnly) {
      return;
    }

    this.submitData.push(
      new SubmitContributorData({
        ...head(
          filter(
            this.existingContributorsList,
            (contributors: IIepContributor) =>
              contributors.personSk === data.personSk
          )
        ),
        operationType: this.operationTypeRemove,
        iepFormSk: this.iepFormSk,
        sectionId: this.sectionId,
      })
    );
    this.existingContributorsList = filter(
      this.existingContributorsList,
      (contributors: IIepContributor) => contributors.personSk !== data.personSk
    );
    const selected: IIepStaff[] = (this.form?.get('addContributor')?.value ?? []) as IIepStaff[];
    this.form
      ?.get('addContributor')
      ?.setValue(
        selected.filter((staff: IIepStaff) => !this.isSameContributor(staff as unknown as IIepContributor, data as IIepContributor))
      );
  }

  save(): void {
    if (this.isReadOnly) {
      return;
    }

    const selectedContributors: SubmitContributorData[] = (
      get(this.form?.value, 'addContributor') ?? []
    ).filter(
      (d: SubmitContributorData) =>
        !this.existingContributorsList?.some((existing: IIepContributor) =>
          this.isSameContributor(existing, d as unknown as IIepContributor)
        )
    );
    this.submitData.push(
      ...map(
        selectedContributors,
        (d: SubmitContributorData) =>
          new SubmitContributorData({
            ...d,
            actionType: DocumentStatus.SaveForLater,
            operationType: this.operationTypeSave,
            iepFormSk: this.iepFormSk,
            sectionId: this.sectionId,
            lastModuser: this.user.username,
          })
      )
    );
    
    if (!this.progressReportSk) {
      this.notificationService.show({
        content: 'Please save the progress report before adding contributors.',
        hideAfter: dialogTimer,
        position: { horizontal: 'center', vertical: 'bottom' },
        animation: { type: 'slide', duration: dialogAnimationDuration },
        type: { style: 'error', icon: true },
      });
      return;
    }

    this.submitProgressReportContributorDetailsService(this.submitData);
      
  }

  submitProgressReportContributorDetailsService(
    submitRequestdata: SubmitContributorData[]
  ): void {
    const payload: SubmitProgressReportContributorData[] = submitRequestdata.map(
      (contributor: SubmitContributorData) =>
        new SubmitProgressReportContributorData({
          progressReportSk: this.progressReportSk,
          progressMeasurementSk: this.progressMeasurementSk,
          mandateSK: this.mandateSK,
          annualGoalSeq: this.annualGoalSeq,
          personSK: contributor.personSK,
          role: contributor.role,
          lastModuser: contributor.lastModuser ?? this.user?.username,
          lastModTS: contributor.lastModTS ?? new Date(),
          personName: contributor.personName,
          firstName: contributor.firstName,
          lastName: contributor.lastName,
          middleName: contributor.middleName,
          email: contributor.email,
          phone: contributor.phone,
          title: contributor.title,
          operationType: contributor.operationType,
        })
    );

    this.contributorService
      .submitProgressReportContributors(payload)
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.notificationService.show({
            content: NOTIFICATION_MESSAGES.submitSuccess,
            cssClass: 'button-notification',
            hideAfter: dialogTimer,
            position: { horizontal: 'center', vertical: 'bottom' },
            animation: { type: 'slide', duration: dialogAnimationDuration },
            type: { style: 'success', icon: true },
          });
          this.dialog.close();
        },
        error: () => {
          this.notificationService.show({
            content: NOTIFICATION_MESSAGES.submissionFailed,
            hideAfter: dialogTimer,
            position: { horizontal: 'center', vertical: 'bottom' },
            animation: { type: 'slide', duration: dialogAnimationDuration },
            type: { style: 'error', icon: true },
          });
        },
      });
  }

  private isSameContributor(
    left: IIepContributor,
    right: IIepContributor,
  ): boolean {
    const leftPersonSk: string = ((left as any)?.personSk ?? (left as any)?.personSK ?? '') as string;
    const rightPersonSk: string = ((right as any)?.personSk ?? (right as any)?.personSK ?? '') as string;
    if (leftPersonSk && rightPersonSk) {
      return leftPersonSk === rightPersonSk;
    }

    return (
      (left?.firstName ?? '').toLowerCase() ===
        (right?.firstName ?? '').toLowerCase() &&
      (left?.lastName ?? '').toLowerCase() ===
        (right?.lastName ?? '').toLowerCase()
    );
  }
}
